import { PDFDocument } from 'pdf-lib';
import { useCallback } from 'react';
import { useBoxApi, useMutateBoxItems } from 'hooks/api/box';
import { messages } from 'i18n';
import { extractExtensionFromFile, getFileNameWithoutExtension, logger } from 'utils';
import { useGetBoxPdfRepresentationAsBlob } from 'utils/box-utils';
import { TRUNCATE_PDF_SIZE_LIMIT } from 'broker/components/FilesExplorer/utils';

export type PdfTruncateProgress =
  | {
      phase: 'preparing';
    }
  | {
      phase: 'downloading';
    }
  | {
      phase: 'loading';
    }
  | {
      phase: 'truncating';
      pagesProcessed: number;
      totalPages: number;
    }
  | {
      phase: 'uploading';
    };

export type PdfTruncateOptions = {
  onProgress?: (progress: PdfTruncateProgress) => void;
};

// Allow the browser to paint the UI and avoid blocking the main thread
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

async function truncatePdf(
  originalPdf: PDFDocument,
  onProgress?: PdfTruncateOptions['onProgress'],
): Promise<Uint8Array> {
  const truncatedPdf = await PDFDocument.create();

  let lastGoodBytes: Uint8Array = new Uint8Array();
  const originalDocumentPageIndices = originalPdf.getPageIndices();
  const totalPages = originalDocumentPageIndices.length;

  // Truncate the pdf by copying pages one by one until the pdf is less than 10MB
  // Possible optimization:
  // 1. After adding 5 pages and we're still under 10MB, calculate the average page size
  // and add the number of pages needed to reach 10MB. If we we oversize, remove from end one by one until we're under 10MB
  let pagesProcessed = 0;
  for (const pageIndex of originalDocumentPageIndices) {
    const [copiedPage] = await truncatedPdf.copyPages(originalPdf, [pageIndex]);
    truncatedPdf.addPage(copiedPage);
    const pageBytes = await truncatedPdf.save();
    pagesProcessed += 1;

    onProgress?.({ phase: 'truncating', pagesProcessed, totalPages });
    await yieldToBrowser();

    if (pageBytes.length < TRUNCATE_PDF_SIZE_LIMIT) {
      lastGoodBytes = pageBytes;
    } else {
      // note that if the first page is already too large, we will return an empty array.
      // the caller depends on this to determine if the pdf could be truncated.
      return lastGoodBytes;
    }
  }

  return lastGoodBytes;
}

export function useCreateTruncatedPdf() {
  const { getBoxItemById } = useBoxApi();
  const { downloadFileAsBlob, uploadFileWithMetadata } = useMutateBoxItems();
  const getBoxPdfRepresentationAsBlob = useGetBoxPdfRepresentationAsBlob();

  return useCallback(
    async (boxFileId: string, options?: PdfTruncateOptions) => {
      options?.onProgress?.({ phase: 'preparing' });
      await yieldToBrowser();

      // Items coming from folder listings may not have `parent` populated (depends on requested fields).
      // Refetch the full file to ensure we have `parent.id` for uploading the truncated version back
      // into the same folder as the original.
      const boxItem = await getBoxItemById({ id: boxFileId, options: { includeMetadata: true } });
      if (!boxItem?.parent?.id) {
        throw new Error(`Missing parent folder for Box item id: ${boxFileId}`);
      }

      const sourceFileName = boxItem.name;
      const isPdf = extractExtensionFromFile(boxItem.name) === 'pdf';

      options?.onProgress?.({ phase: 'downloading' });
      await yieldToBrowser();

      const fileBlob = isPdf ? await downloadFileAsBlob(boxFileId) : await getBoxPdfRepresentationAsBlob(boxFileId);

      if (!fileBlob) {
        logger.log('error', { message: `Failed to get file blob for id: ${boxFileId}` });
        throw new Error(`Failed to download ${boxItem.name}`);
      }

      options?.onProgress?.({ phase: 'loading' });
      await yieldToBrowser();

      const originalPdf = await PDFDocument.load(await fileBlob.arrayBuffer());
      // serialize the original pdf to bytes (save doesn't really save, it just serializes)
      let pdfBytes = await originalPdf.save();

      if (pdfBytes.length < TRUNCATE_PDF_SIZE_LIMIT) {
        return {
          didTruncate: false,
          boxItem,
          alreadyTruncatedMessage: messages.fileExplorer.fileAlreadyTruncated,
        };
      }

      pdfBytes = await truncatePdf(originalPdf, options?.onProgress);
      if (pdfBytes.length === 0) {
        throw new Error(messages.fileExplorer.fileTooLargeToTruncate(TRUNCATE_PDF_SIZE_LIMIT));
      }

      const newFileName = `${getFileNameWithoutExtension(sourceFileName)}_truncated.pdf`;

      options?.onProgress?.({ phase: 'uploading' });
      await yieldToBrowser();

      const newBoxItem = await uploadFileWithMetadata({
        parentFolderId: boxItem.parent.id,
        fileName: newFileName,
        file: new Blob([pdfBytes], { type: 'application/pdf' }),
        retryWithDate: true,
        metadata: boxItem.metadata || {},
      });

      return {
        didTruncate: true,
        boxItem: newBoxItem,
        successMessage: messages.fileExplorer.truncateSuccess,
      };
    },
    [downloadFileAsBlob, getBoxItemById, getBoxPdfRepresentationAsBlob, uploadFileWithMetadata],
  );
}
